import React from 'react';
import './FormationDiagram.css';

const ROLE_COLORS = {
  GK: '#fbbf24',
  DEF: '#3b82f6',
  MID: '#22c55e',
  FWD: '#ef4444',
  OPP: '#f97316',
};

function resolveColor(player) {
  if (player.color) return player.color;
  if (player.roleType && ROLE_COLORS[player.roleType]) return ROLE_COLORS[player.roleType];
  return '#94a3b8';
}

function FieldMarkings() {
  return (
    <g className="formation-diagram__markings" aria-hidden="true">
      <rect x="0" y="0" width="100" height="65" className="formation-diagram__grass" />
      <rect x="0" y="17.5" width="16" height="30" className="formation-diagram__line" fill="none" />
      <rect x="0" y="26" width="6" height="13" className="formation-diagram__line" fill="none" />
      <circle cx="11" cy="32.5" r="0.8" className="formation-diagram__line" fill="none" />
      <line x1="50" y1="0" x2="50" y2="65" className="formation-diagram__line" />
      <circle cx="50" cy="32.5" r="9" className="formation-diagram__line" fill="none" />
      <circle cx="50" cy="32.5" r="0.8" className="formation-diagram__dot" />
      <rect x="84" y="17.5" width="16" height="30" className="formation-diagram__line" fill="none" />
      <rect x="94" y="26" width="6" height="13" className="formation-diagram__line" fill="none" />
      <circle cx="89" cy="32.5" r="0.8" className="formation-diagram__line" fill="none" />
    </g>
  );
}

function MovementArrow({ arrow, animated = false }) {
  const { from, to, label, style = 'pass', dashed, pulse } = arrow;
  const isRun = style === 'run';
  const stroke = isRun ? '#f97316' : '#e2e8f0';
  const dashArray = dashed || isRun ? '2.5 2' : undefined;
  const arrowClass = [
    'formation-diagram__arrow',
    `formation-diagram__arrow--${style}`,
    animated && isRun ? 'formation-diagram__arrow--animated' : '',
    animated && pulse ? 'formation-diagram__arrow--pulse' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <g className={arrowClass}>
      <defs>
        <marker
          id={`arrowhead-${style}-${from.x}-${from.y}-${to.x}-${to.y}`}
          markerWidth="6"
          markerHeight="6"
          refX="5"
          refY="3"
          orient="auto"
        >
          <polygon points="0 0, 6 3, 0 6" fill={stroke} />
        </marker>
      </defs>
      <line
        x1={from.x}
        y1={from.y}
        x2={to.x}
        y2={to.y}
        stroke={stroke}
        strokeWidth={isRun ? 0.55 : 0.7}
        strokeDasharray={dashArray}
        markerEnd={`url(#arrowhead-${style}-${from.x}-${from.y}-${to.x}-${to.y})`}
      />
      {label && (
        <text
          x={(from.x + to.x) / 2}
          y={(from.y + to.y) / 2 - 1.2}
          className="formation-diagram__arrow-label"
        >
          {label}
        </text>
      )}
    </g>
  );
}

function PlayerMarker({ player, animated = false, markerClass = 'formation-diagram__player' }) {
  const color = resolveColor(player);
  const hasMotion = animated && player.animateFrom;
  const playerClass = [
    markerClass,
    hasMotion ? `${markerClass}--animated` : '',
    hasMotion ? `${markerClass}--motion` : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <g className={playerClass} transform={`translate(${player.x}, ${player.y})`}>
      {hasMotion && (
        <animateTransform
          attributeName="transform"
          type="translate"
          values={`${player.animateFrom.x} ${player.animateFrom.y}; ${player.x} ${player.y}; ${player.x} ${player.y}`}
          keyTimes="0;0.45;1"
          dur="2.8s"
          repeatCount="indefinite"
          calcMode="spline"
          keySplines="0.42 0 0.58 1;0.42 0 0.58 1"
        />
      )}
      <circle r="3.2" fill={color} className="formation-diagram__player-dot" />
      <text y="0.9" className="formation-diagram__player-label">
        {player.label}
      </text>
      {player.sublabel && (
        <text y="5.2" className="formation-diagram__player-sublabel">
          {player.sublabel}
        </text>
      )}
    </g>
  );
}

/**
 * Half-pitch formation diagram for blog articles.
 * Coordinates use viewBox 0–100 (width) × 0–65 (height); goal on the left.
 */
export default function FormationDiagram({
  title,
  caption,
  legend,
  players = [],
  opponents = [],
  arrows = [],
  animated = false,
}) {
  const wrapClass = ['formation-diagram__canvas-wrap', animated ? 'formation-diagram__canvas-wrap--animated' : '']
    .filter(Boolean)
    .join(' ');

  return (
    <figure className={`formation-diagram${animated ? ' formation-diagram--animated' : ''}`}>
      {title && <figcaption className="formation-diagram__title">{title}</figcaption>}
      <div className={wrapClass}>
        <svg
          viewBox="0 0 100 65"
          className="formation-diagram__svg"
          role="img"
          aria-label={caption || title || 'Soccer formation diagram'}
        >
          <FieldMarkings />
          {arrows.map((arrow, index) => (
            <MovementArrow key={`arrow-${index}`} arrow={arrow} animated={animated} />
          ))}
          {opponents.map((player) => (
            <PlayerMarker
              key={`opp-${player.label}-${player.x}-${player.y}`}
              player={{ ...player, roleType: player.roleType || 'OPP' }}
              animated={animated}
              markerClass="formation-diagram__opponent"
            />
          ))}
          {players.map((player) => (
            <PlayerMarker
              key={`${player.label}-${player.x}-${player.y}`}
              player={player}
              animated={animated}
            />
          ))}
        </svg>
      </div>
      {caption && <p className="formation-diagram__caption">{caption}</p>}
      {legend?.length > 0 && (
        <ul className="formation-diagram__legend">
          {legend.map((item) => {
            const color = typeof item === 'string' ? '#94a3b8' : item.color;
            const label = typeof item === 'string' ? item : item.label;
            return (
              <li key={label}>
                <span
                  className="formation-diagram__legend-swatch"
                  style={{ background: color }}
                  aria-hidden="true"
                />
                {label}
              </li>
            );
          })}
        </ul>
      )}
    </figure>
  );
}
